import { useEffect } from "react"
import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { Skeleton } from "@/components/ui/skeleton"
import { PaywallDialog } from "@/components/paywall-dialog"
import Footer from "@/components/footer-1"
import { CompanyRail } from "@/components/company-rail"
import { JobApplySection } from "@/components/job-apply-section"
import { RoleStoryScroll } from "@/components/role-story-scroll"
import { HiringRegionChips } from "@/components/hiring-region-picker"
import { useJobApplication } from "@/hooks/use-job-application"
import { jobWorkModeLabel } from "@/lib/employment"
import { errorMessage } from "@/lib/errors"
import { formatSalary } from "@/lib/salary"
import { formatShortDate } from "@/lib/dates"
import { toFixedRolePanels } from "@/lib/job-description"
import {
  parseJobRouteId,
  ReferSomeonePanel,
  ShareJobButton,
  useSavedJobs,
  useTrackJobPageView,
} from "@/features/jobs"
import { Bookmark } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

export const Route = createFileRoute("/jobs/$jobId")({
  beforeLoad: ({ params }) => {
    const canonical = parseJobRouteId(params.jobId)
    if (canonical && canonical !== params.jobId) {
      throw redirect({
        to: "/jobs/$jobId",
        params: { jobId: canonical },
        replace: true,
      })
    }
  },
  component: JobDetailContent,
})

function JobDetailContent() {
  const { jobId: jobIdParam } = Route.useParams()
  const jobId = parseJobRouteId(jobIdParam) as Id<"jobs"> | null
  const job = useQuery(api.jobs.getPublic, jobId ? { jobId } : "skip")
  const application = useJobApplication(jobId)
  const saved = useSavedJobs()
  const pageTitle = job?.title
  const pageCompany = job?.company?.companyName

  useTrackJobPageView({
    jobId,
    title: pageTitle,
    companyId: job?.company?.companyId,
    companyName: pageCompany,
  })

  useEffect(() => {
    if (!pageTitle) return
    const previous = document.title
    const companyName = pageCompany?.trim()
    document.title = companyName
      ? `${pageTitle} at ${companyName} — Hire`
      : `${pageTitle} — Hire`
    return () => {
      document.title = previous
    }
  }, [pageTitle, pageCompany])

  const toggleSave = async () => {
    if (!jobId) return
    try {
      const result = await saved.toggle(jobId)
      toast.success(result.saved ? "Saved — find it under Saved in your desk" : "Removed from saved jobs")
    } catch (e) {
      toast.error(errorMessage(e, "Could not save job"))
    }
  }

  if (!jobId || job === null) {
    return (
      <div className="mkt-page home-wf">
        <div className="mkt-page-shell mkt-page-shell--narrow">
          <p>Job not found or closed.</p>
          <Link to="/jobs/browse" className="home-wf-btn home-wf-btn--outline mt-4 inline-flex">
            Back to jobs
          </Link>
        </div>
        <Footer />
      </div>
    )
  }
  if (job === undefined) {
    return (
      <div className="mkt-page home-wf">
        <div className="mkt-page-shell mkt-page-shell--detail">
          <Skeleton className="h-48 w-full" />
        </div>
        <Footer />
      </div>
    )
  }

  const company = job.company
  const rolePanels = toFixedRolePanels(job.description)
  const workMode = jobWorkModeLabel(job.workMode)

  return (
    <div className="mkt-page home-wf">
      <div className="mkt-page-shell mkt-page-shell--detail">
        <Link
          to="/jobs/browse"
          className="mb-6 inline-block text-sm font-bold text-[color:var(--wf-mute)] underline-offset-4 hover:text-[color:var(--wf-ink)] hover:underline"
        >
          ← All roles
        </Link>

        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_17.5rem] lg:items-start lg:gap-14">
          <div className="min-w-0">
            <header>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="mkt-page-title">{job.title}</h1>
                  {job.featured && (
                    <p className="mt-2 text-sm font-bold text-[color:var(--wf-lime-text)]">
                      Featured on the HSR board
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <ShareJobButton
                    jobId={job._id}
                    title={job.title}
                    companyName={company?.companyName}
                  />
                  {saved.isSeeker && (
                    <button
                      type="button"
                      className="mkt-row-action inline-flex shrink-0 items-center gap-1.5"
                      aria-pressed={saved.isSaved(jobId)}
                      onClick={() => void toggleSave()}
                    >
                      <Bookmark
                        className={cn(
                          "size-4",
                          saved.isSaved(jobId) &&
                            "fill-[color:var(--wf-lime-text)] text-[color:var(--wf-lime-text)]",
                        )}
                        aria-hidden
                      />
                      {saved.isSaved(jobId) ? "Saved" : "Save"}
                    </button>
                  )}
                </div>
              </div>
              {company && (
                <p className="mkt-page-sub">
                  <Link
                    to="/companies/$companyId"
                    params={{ companyId: company.companyId as string }}
                    className="font-bold text-[color:var(--wf-ink)] underline-offset-4 hover:underline"
                  >
                    {company.companyName}
                  </Link>
                  {company.companyOneLiner ? ` — ${company.companyOneLiner}` : ""}
                </p>
              )}

              <dl className="mkt-job-facts">
                <div>
                  <dt>Location</dt>
                  <dd>{job.location}</dd>
                </div>
                <div>
                  <dt>Work mode</dt>
                  <dd>{workMode ?? "—"}</dd>
                </div>
                <div>
                  <dt>Type</dt>
                  <dd>{job.employmentType}</dd>
                </div>
                <div>
                  <dt>Compensation</dt>
                  <dd>{formatSalary(job) || "Not listed"}</dd>
                </div>
              </dl>

              {(job.openToRelocation ||
                (job.remoteRegions && job.remoteRegions.length > 0)) && (
                <div className="mt-3">
                  {job.openToRelocation && (
                    <dl className="mkt-job-facts">
                      <div>
                        <dt>Relocation</dt>
                        <dd>
                          Open to relocating candidates
                          {job.relocationAssistance ? " · assistance offered" : ""}
                        </dd>
                      </div>
                    </dl>
                  )}
                  {job.remoteRegions && job.remoteRegions.length > 0 && (
                    <div className={job.openToRelocation ? "mt-3" : ""}>
                      <p className="text-[0.58rem] font-bold tracking-[0.1em] text-[color:var(--wf-mute)] uppercase">
                        Hiring regions
                      </p>
                      <HiringRegionChips
                        regions={job.remoteRegions}
                        className="mt-1.5"
                      />
                    </div>
                  )}
                </div>
              )}

              {(job.remoteCulture || job.collaborationHoursStart) && (
                <dl className="mkt-job-facts mkt-job-facts--remote">
                  {job.remoteCulture && (
                    <div>
                      <dt>Remote culture</dt>
                      <dd>
                        {job.remoteCulture === "in_person_hub"
                          ? "In-person hub"
                          : "Mostly remote"}
                      </dd>
                    </div>
                  )}
                  {job.collaborationHoursStart && job.collaborationHoursEnd && (
                    <div>
                      <dt>Collaboration hours</dt>
                      <dd>
                        {job.collaborationHoursStart}–{job.collaborationHoursEnd}
                        {job.collaborationTimezone ? ` ${job.collaborationTimezone}` : ""}
                      </dd>
                    </div>
                  )}
                </dl>
              )}

              {job.coworkers.length > 0 && (
                <div className="mkt-job-coworkers">
                  <p className="text-[0.58rem] font-bold tracking-[0.1em] text-[color:var(--wf-mute)] uppercase">
                    You'll work closely with
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {job.coworkers.map((c, i) => (
                      <span
                        key={`${i}-${c.name}`}
                        className="mkt-chip mkt-chip--outline"
                      >
                        {c.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </header>

            <RoleStoryScroll
              panels={rolePanels}
              skills={job.skills}
              resetKey={jobId}
              onReadComplete={application.setStoryRead}
            />

            {job.linkedHackathon && (
              <section className="mkt-job-band mkt-job-hackathon">
                <h2>Hiring through a hackathon</h2>
                <p>
                  <Link
                    to="/hackathons/$hackathonId"
                    params={{ hackathonId: job.linkedHackathon._id }}
                    className="font-bold text-[color:var(--wf-lime-text)] underline-offset-4 hover:underline"
                  >
                    {job.linkedHackathon.title}
                  </Link>
                </p>
                <p className="mkt-job-hackathon__when">
                  {formatShortDate(job.linkedHackathon.startsAt, true)}
                </p>
              </section>
            )}

            <JobApplySection application={application} />

            {application.profile?.role === "seeker" && (
              <ReferSomeonePanel
                jobId={job._id}
                jobTitle={job.title}
                variant="band"
              />
            )}

            <PaywallDialog
              open={application.payOpen}
              onOpenChange={application.setPayOpen}
              kind="seeker_annual"
              onPaid={application.applyAfterPayment}
            />
          </div>

          {company && (
            <CompanyRail
              company={company}
              otherRoles={job.otherRoles}
              companyEvents={job.companyEvents}
            />
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}
